/** @typedef {'futureBirth' | 'invalidDate' | 'ageBeforeBirth' | 'invalidDifference'} AgeErrorCode */

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_MINUTE = 60_000;
const DAYS_PER_YEAR = 365.2425;

/**
 * @param {number} year
 */
export function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * @param {string} dateStr YYYY-MM-DD
 * @returns {Date | null}
 */
export function parseDateInput(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * @param {Date} date
 */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * @param {Date} date
 */
export function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Birthday in a given year, handling Feb 29 → Feb 28 in non-leap years.
 * @param {Date} birth
 * @param {number} year
 */
export function getBirthdayInYear(birth, year) {
  const month = birth.getMonth();
  let day = birth.getDate();
  if (month === 1 && day === 29 && !isLeapYear(year)) {
    day = 28;
  }
  return new Date(year, month, day);
}

/**
 * @param {Date} birth
 * @param {Date} at
 */
export function getNextBirthday(birth, at) {
  const atDay = startOfDay(at);
  let candidate = getBirthdayInYear(birth, atDay.getFullYear());
  if (candidate <= atDay) {
    candidate = getBirthdayInYear(birth, atDay.getFullYear() + 1);
  }
  return candidate;
}

/**
 * @param {Date} from
 * @param {Date} to
 */
export function diffYmd(from, to) {
  const start = startOfDay(from);
  const end = startOfDay(to);

  if (end < start) {
    return { years: 0, months: 0, days: 0, negative: true };
  }

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months, days, negative: false };
}

/**
 * @param {Date} birth
 * @param {Date} at
 * @param {Date} [today] defaults to current date for future-birth validation
 */
export function calculateAge(birth, at, today = new Date()) {
  const birthDay = startOfDay(birth);
  const atDay = startOfDay(at);
  const todayDay = startOfDay(today);

  if (birthDay > todayDay) {
    return { ok: false, error: /** @type {AgeErrorCode} */ ("futureBirth") };
  }
  if (atDay < birthDay) {
    return { ok: false, error: /** @type {AgeErrorCode} */ ("ageBeforeBirth") };
  }

  const { years, months, days } = diffYmd(birthDay, atDay);
  const msDiff = atDay.getTime() - birthDay.getTime();
  const totalDays = Math.floor(msDiff / MS_PER_DAY);
  const totalWeeks = Math.floor(totalDays / 7);
  const totalHours = Math.floor(msDiff / MS_PER_HOUR);
  const totalMinutes = Math.floor(msDiff / MS_PER_MINUTE);
  const totalMonths =
    (atDay.getFullYear() - birthDay.getFullYear()) * 12 +
    (atDay.getMonth() - birthDay.getMonth()) -
    (atDay.getDate() < birthDay.getDate() ? 1 : 0);
  const totalYears = totalDays / DAYS_PER_YEAR;

  const nextBirthday = getNextBirthday(birthDay, atDay);
  const daysUntilBirthday = Math.floor(
    (nextBirthday.getTime() - atDay.getTime()) / MS_PER_DAY
  );
  const untilBirthday = diffYmd(atDay, nextBirthday);

  return {
    ok: true,
    exact: { years, months, days },
    totals: {
      years: totalYears,
      months: totalMonths,
      weeks: totalWeeks,
      days: totalDays,
      hours: totalHours,
      minutes: totalMinutes,
    },
    birthday: {
      dayOfWeek: birthDay.getDay(),
      nextDate: nextBirthday,
      daysUntil: daysUntilBirthday,
      monthsUntil: untilBirthday.months,
      daysUntilPart: untilBirthday.days,
      isToday: daysUntilBirthday === 0,
    },
    isSameDay: totalDays === 0,
  };
}

/**
 * @param {Date} dob1
 * @param {Date} dob2
 */
export function calculateAgeDifference(dob1, dob2) {
  const first = startOfDay(dob1);
  const second = startOfDay(dob2);

  if (first.getTime() === second.getTime()) {
    return {
      ok: true,
      sameDay: true,
      olderIndex: null,
      diff: { years: 0, months: 0, days: 0 },
      totalDays: 0,
    };
  }

  const older = first < second ? first : second;
  const younger = first < second ? second : first;
  const diff = diffYmd(older, younger);
  const totalDays = Math.floor((younger.getTime() - older.getTime()) / MS_PER_DAY);

  return {
    ok: true,
    sameDay: false,
    olderIndex: first < second ? 1 : 2,
    diff,
    totalDays,
  };
}

/**
 * @param {string | null | undefined} dateStr
 * @param {Date} [today]
 */
export function validateBirthDate(dateStr, today = new Date()) {
  const parsed = parseDateInput(dateStr ?? "");
  if (!parsed) return { valid: false, error: /** @type {AgeErrorCode} */ ("invalidDate") };
  if (startOfDay(parsed) > startOfDay(today)) {
    return { valid: false, error: /** @type {AgeErrorCode} */ ("futureBirth"), date: parsed };
  }
  return { valid: true, date: parsed };
}

/**
 * @param {string | null | undefined} dateStr
 */
export function validateAgeAtDate(dateStr) {
  const parsed = parseDateInput(dateStr ?? "");
  if (!parsed) return { valid: false, error: /** @type {AgeErrorCode} */ ("invalidDate") };
  return { valid: true, date: parsed };
}
